import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

/**
 * Diálogos de la app (0.28.1) — lo que reemplaza a alert/confirm/prompt del navegador.
 *
 * "TODOS los pop ups deben de tener el mismo estilo del aplicativo al que pertenecen para no romper la
 * experiencia." Un `confirm()` nativo sale con la fuente y los botones del sistema, dice el dominio de
 * la página arriba y en algunos celulares hasta ofrece "no mostrar más diálogos" — y si alguien lo
 * marca, el siguiente `confirm()` devuelve `false` en silencio y el botón "Quitar" deja de hacer nada.
 *
 * API imperativa con promesas, para que el cambio en cada pantalla sea de una línea:
 *   if (!(await confirmar({ titulo, mensaje, confirmar: 'Quitar', peligro: true }))) return;
 *   await avisar({ titulo: 'No se pudo guardar', mensaje: textoDeError(e), tipo: 'error' });
 *   const nombre = await pedirTexto({ titulo, valor });   // string | null (null = canceló)
 *
 * `DialogoHost` se monta UNA vez en la raíz (App.tsx). Los pedidos se forman en fila: se enseña uno a la
 * vez y el siguiente sale cuando se cierra el anterior. Va por encima de todo lo demás (z-index 5000):
 * los diálogos de edición (60), la galería (70) y las gráficas ampliadas (1000).
 *
 * Teclado: Escape o clic en el fondo = cancelar; Enter = el botón principal (si el foco está en otro
 * botón del diálogo, Enter oprime ése); Tab no se sale del diálogo. El foco empieza en el botón
 * principal (o en el campo, en pedirTexto) y regresa a donde estaba al cerrar.
 */

export type TipoAviso = 'info' | 'ok' | 'error';

export interface OpcionesConfirmar {
  titulo?: string;
  mensaje: string;
  /** Texto del botón principal. Para algo que quita o borra, el verbo: "Quitar", "Descartar". */
  confirmar?: string;
  cancelar?: string;
  /** Acción destructiva: el botón principal va en rojo. */
  peligro?: boolean;
}
export interface OpcionesAvisar {
  titulo?: string;
  mensaje: string;
  tipo?: TipoAviso;
  boton?: string;
}
export interface OpcionesPedirTexto {
  titulo?: string;
  mensaje?: string;
  etiqueta?: string;
  valor?: string;
  placeholder?: string;
  confirmar?: string;
  cancelar?: string;
  /** Varios renglones: Enter hace salto de línea y Ctrl/Cmd+Enter acepta. */
  multilinea?: boolean;
  /** No deja aceptar con el campo vacío. */
  requerido?: boolean;
  maxLength?: number;
  /** Un PIN (0.47.0): campo de contraseña numérico, sólo dígitos (4 a 6), sin autocompletar. */
  pin?: boolean;
  /** Un error que se enseña en rojo debajo del campo (p. ej. "PIN incorrecto: te quedan 3 intentos"). */
  error?: string;
  /** Lo que quita o descarta y pide un motivo ("Descartar…"): el botón principal va en rojo, como en `confirmar`. */
  peligro?: boolean;
}

type Pedido =
  | { id: number; clase: 'confirmar'; o: OpcionesConfirmar; fin: (v: boolean) => void }
  | { id: number; clase: 'avisar'; o: OpcionesAvisar; fin: () => void }
  | { id: number; clase: 'texto'; o: OpcionesPedirTexto; fin: (v: string | null) => void };

// ------------------------------------------------------------ la fila (fuera de React)
let cola: Pedido[] = [];
let siguienteId = 1;
let hosts = 0;
const oyentes = new Set<() => void>();
const notificar = () => oyentes.forEach((f) => f());
const suscribir = (f: () => void) => { oyentes.add(f); return () => { oyentes.delete(f); }; };
const primero = () => cola[0] ?? null;
const sacar = (id: number) => { cola = cola.filter((p) => p.id !== id); notificar(); };

function encolar<T>(armar: (fin: (v: T) => void) => Pedido, porDefecto: T): Promise<T> {
  return new Promise<T>((resolve) => {
    // Sin host montado nadie lo vería y la promesa quedaría colgada para siempre: se contesta como
    // "cancelar" y se dice en consola. No debería pasar (el host vive en App.tsx).
    if (hosts === 0) { console.error('Dialogo: no hay <DialogoHost /> montado; se contesta por defecto.'); resolve(porDefecto); return; }
    cola = [...cola, armar(resolve)];
    notificar();
  });
}

export function confirmar(o: OpcionesConfirmar | string): Promise<boolean> {
  const op = typeof o === 'string' ? { mensaje: o } : o;
  return encolar<boolean>((fin) => ({ id: siguienteId++, clase: 'confirmar', o: op, fin }), false);
}
export function avisar(o: OpcionesAvisar | string): Promise<void> {
  const op = typeof o === 'string' ? { mensaje: o } : o;
  return encolar<void>((fin) => ({ id: siguienteId++, clase: 'avisar', o: op, fin: () => fin() }), undefined);
}
export function pedirTexto(o: OpcionesPedirTexto | string = {}): Promise<string | null> {
  const op = typeof o === 'string' ? { titulo: o } : o;
  return encolar<string | null>((fin) => ({ id: siguienteId++, clase: 'texto', o: op, fin }), null);
}

/**
 * El texto de un error para enseñarlo a una persona. El mensaje del servidor manda; un "Error 500" pelón
 * (el API no mandó `error`) o un "Failed to fetch" no le dicen nada a nadie, así que se explican.
 */
export function textoDeError(e: unknown, respaldo = 'No se pudo completar la acción.'): string {
  const msg = (e instanceof Error ? e.message : typeof e === 'string' ? e : '').trim();
  const status = typeof (e as { status?: unknown })?.status === 'number' ? (e as { status: number }).status : null;
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(msg)) return `${respaldo}\nNo hubo conexión con el servidor. Revisa el internet e intenta de nuevo.`;
  if (msg && !/^error \d{3}$/i.test(msg)) return msg;
  if (status) return `${respaldo}\nEl servidor contestó con un error (HTTP ${status}). Intenta de nuevo en un momento.`;
  return respaldo;
}

// ------------------------------------------------ la burbuja de validación del navegador
/**
 * "Please fill out this field" (en inglés y con el estilo del sistema) también es un pop up nativo: sale con
 * cualquier `required` / `type="email"`. Se escucha `invalid` en TODO el documento, se suprime y se dice en
 * el diálogo de la app qué falta; el campo queda con [aria-invalid] (rojo en theme.css) y con el foco.
 */
function etiquetaDe(el: HTMLInputElement): string {
  let t = '';
  if (el.id) t = document.querySelector(`label[for="${el.id}"]`)?.textContent || '';
  if (!t) t = el.closest('label')?.textContent || '';
  t = (t || el.getAttribute('aria-label') || el.placeholder || el.name || 'este dato').replace(/\*/g, '').replace(/[:：]\s*$/, '').replace(/\s+/g, ' ').trim();
  return t.length > 60 ? `${t.slice(0, 57)}…` : t;
}
function motivoDe(el: HTMLInputElement): string {
  const v = el.validity; const n = etiquetaDe(el);
  if (v.valueMissing) return el.type === 'checkbox' || el.type === 'radio' ? `Elige: ${n}` : `Falta: ${n}`;
  if (v.typeMismatch && el.type === 'email') return `${n}: escribe un correo válido`;
  if (v.patternMismatch) return `${n}: revisa el formato`;
  if (v.tooShort) return `${n}: es muy corto`;
  return `${n}: revisa el dato`;
}
function instalarValidacion(): () => void {
  let lista: HTMLInputElement[] = []; let pendiente = false;
  const invalido = (e: Event) => {
    const el = e.target as HTMLInputElement; if (!el || !el.validity) return;
    e.preventDefault();
    el.setAttribute('aria-invalid', 'true');
    if (!lista.some((x) => x === el || (el.type === 'radio' && x.type === 'radio' && x.name === el.name))) lista.push(el);
    if (pendiente) return; pendiente = true;
    setTimeout(() => {
      const campos = lista; lista = []; pendiente = false;
      // El foco va al primer campo DESPUÉS de que el diálogo devuelva el suyo (su limpieza corre al desmontar).
      avisar({ tipo: 'error', titulo: 'Revisa los datos', mensaje: campos.map(motivoDe).join('\n') }).then(() => { setTimeout(() => { try { campos[0]?.focus(); } catch { /* ya no existe */ } }, 30); });
    }, 0);
  };
  const limpiar = (e: Event) => { const el = e.target as HTMLInputElement; if (el?.getAttribute?.('aria-invalid') === 'true' && el.validity?.valid) el.removeAttribute('aria-invalid'); };
  document.addEventListener('invalid', invalido, true);
  document.addEventListener('input', limpiar, true);
  document.addEventListener('change', limpiar, true);
  return () => { document.removeEventListener('invalid', invalido, true); document.removeEventListener('input', limpiar, true); document.removeEventListener('change', limpiar, true); };
}

// ------------------------------------------------------------ el host
export function DialogoHost() {
  const actual = useSyncExternalStore(suscribir, primero, primero);
  useEffect(() => { hosts++; return () => { hosts--; }; }, []);
  useEffect(() => instalarValidacion(), []);
  if (!actual || typeof document === 'undefined') return null;
  return createPortal(<Caja key={actual.id} p={actual} />, document.body);
}

const ENFOCABLES = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Caja({ p }: { p: Pedido }) {
  const idTitulo = useId();
  const idMensaje = useId();
  const cajaRef = useRef<HTMLDivElement>(null);
  const principalRef = useRef<HTMLButtonElement>(null);
  const campoRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const abajoEnFondo = useRef(false);
  const cerrado = useRef(false);
  const [texto, setTexto] = useState(p.clase === 'texto' ? p.o.valor ?? '' : '');
  const falta = p.clase === 'texto' && ((!!p.o.requerido && !texto.trim()) || (!!p.o.pin && !/^\d{4,6}$/.test(texto)));

  const cerrar = (aceptado: boolean) => {
    if (cerrado.current) return;
    if (aceptado && falta) { campoRef.current?.focus(); return; }
    cerrado.current = true;
    if (p.clase === 'confirmar') p.fin(aceptado);
    else if (p.clase === 'avisar') p.fin();
    else p.fin(aceptado ? texto : null);
    sacar(p.id);
  };
  // El manejador de teclado vive fuera del render: siempre llama a la versión con el texto de hoy.
  const cerrarRef = useRef(cerrar);
  cerrarRef.current = cerrar;

  // Foco al abrir (y de regreso al cerrar).
  useEffect(() => {
    const antes = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      if (p.clase === 'texto' && campoRef.current) { campoRef.current.focus(); campoRef.current.select(); }
      else principalRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(t);
      if (antes && antes !== document.body && document.contains(antes)) { try { antes.focus({ preventScroll: true }); } catch { /* nada */ } }
    };
  }, []);

  // Teclado: en fase de captura en window, para que la pantalla de atrás no se entere de Escape/Enter.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const caja = cajaRef.current;
      if (!caja) return;
      const dentro = e.target instanceof Node && caja.contains(e.target);
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); cerrarRef.current(false); return; }
      if (e.key === 'Enter') {
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (dentro && tag === 'BUTTON') return;                                    // Enter oprime el botón con foco
        if (dentro && tag === 'TEXTAREA' && !(e.ctrlKey || e.metaKey)) return;     // salto de línea
        e.preventDefault(); e.stopImmediatePropagation();
        if (!e.repeat) cerrarRef.current(true);                                   // dejar Enter apretado no confirma
        return;
      }
      if (e.key === 'Tab') {
        const lista = Array.from(caja.querySelectorAll<HTMLElement>(ENFOCABLES));
        if (!lista.length) return;
        const i = lista.indexOf(document.activeElement as HTMLElement);
        if (!dentro || i === -1) { e.preventDefault(); lista[0].focus(); return; }
        if (e.shiftKey && i === 0) { e.preventDefault(); lista[lista.length - 1].focus(); }
        else if (!e.shiftKey && i === lista.length - 1) { e.preventDefault(); lista[0].focus(); }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  let titulo: string; let mensaje: string | undefined; let ok: string; let cancelar: string | null;
  let peligro = false; let icono: Icono;
  if (p.clase === 'confirmar') {
    titulo = p.o.titulo || '¿Continuar?'; mensaje = p.o.mensaje;
    peligro = !!p.o.peligro; ok = p.o.confirmar || (peligro ? 'Sí, continuar' : 'Aceptar'); cancelar = p.o.cancelar || 'Cancelar';
    icono = peligro ? 'peligro' : 'pregunta';
  } else if (p.clase === 'avisar') {
    const tipo = p.o.tipo || 'info';
    titulo = p.o.titulo || (tipo === 'error' ? 'No se pudo completar la acción' : tipo === 'ok' ? 'Listo' : 'Aviso');
    mensaje = p.o.mensaje; ok = p.o.boton || 'Entendido'; cancelar = null; icono = tipo;
  } else {
    titulo = p.o.titulo || 'Escribe el texto'; mensaje = p.o.mensaje;
    peligro = !!p.o.peligro; ok = p.o.confirmar || 'Aceptar'; cancelar = p.o.cancelar || 'Cancelar'; icono = peligro ? 'peligro' : 'texto';
  }
  const tipoDato = p.clase === 'avisar' ? p.o.tipo || 'info' : p.clase === 'confirmar' ? (peligro ? 'peligro' : 'normal') : 'texto';

  return (
    <div className="dlg-fondo" data-dialogo-fondo
      onMouseDown={(e) => { abajoEnFondo.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (abajoEnFondo.current && e.target === e.currentTarget) cerrar(false); abajoEnFondo.current = false; }}>
      <div ref={cajaRef} className="card dlg-caja" role="dialog" aria-modal="true"
        aria-labelledby={idTitulo} aria-describedby={mensaje ? idMensaje : undefined}
        data-dialogo={p.clase} data-tipo={tipoDato}>
        <div className="dlg-cabeza">
          <IconoDialogo tipo={icono} />
          <div id={idTitulo} className="dlg-titulo">{titulo}</div>
        </div>
        {mensaje && <div id={idMensaje} className="dlg-mensaje">{mensaje}</div>}
        {p.clase === 'texto' && (
          <label className="dlg-campo">
            {p.o.etiqueta && <span className="muted">{p.o.etiqueta}</span>}
            {p.o.multilinea
              ? <textarea ref={campoRef} rows={4} value={texto} placeholder={p.o.placeholder} maxLength={p.o.maxLength} onChange={(e) => setTexto(e.target.value)} data-dialogo-campo />
              : p.o.pin
                ? <input ref={campoRef} type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={texto} placeholder={p.o.placeholder || '4 a 6 números'}
                    aria-invalid={p.o.error ? true : undefined} onChange={(e) => setTexto(e.target.value.replace(/\D/g, '').slice(0, 6))} data-dialogo-campo data-dialogo-pin
                    style={{ letterSpacing: '0.35em', fontSize: 20, textAlign: 'center', maxWidth: 200 }} />
                : <input ref={campoRef} value={texto} placeholder={p.o.placeholder} maxLength={p.o.maxLength} onChange={(e) => setTexto(e.target.value)} data-dialogo-campo />}
            {p.o.error && <span role="alert" data-dialogo-error style={{ color: 'var(--danger, #b91c1c)', fontSize: 12.5, lineHeight: 1.4 }}>{p.o.error}</span>}
          </label>
        )}
        <div className="dlg-botones">
          {cancelar && <button type="button" className="btn-ghost" data-dialogo-cancelar onClick={() => cerrar(false)}>{cancelar}</button>}
          <button type="button" ref={principalRef} className={peligro ? 'btn-peligro' : 'btn-primary'} data-dialogo-ok disabled={falta} onClick={() => cerrar(true)}>{ok}</button>
        </div>
      </div>
    </div>
  );
}

// Iconos en SVG (regla de la casa: nada de emojis como iconos), con los colores de estado de la paleta.
type Icono = TipoAviso | 'peligro' | 'pregunta' | 'texto';
const ICONO: Record<Icono, { fondo: string; color: string; trazo: string }> = {
  error: { fondo: 'var(--warning-bg)', color: 'var(--warning)', trazo: 'M12 7.5v5.5M12 16.5v.01' },
  ok: { fondo: 'var(--success-bg)', color: 'var(--success)', trazo: 'M7.5 12.5l3 3 6-6.5' },
  info: { fondo: 'var(--gold-light)', color: 'var(--gold-dark)', trazo: 'M12 11v5.5M12 7.5v.01' },
  pregunta: { fondo: 'var(--gold-light)', color: 'var(--gold-dark)', trazo: 'M9.6 9.3a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.4M12 16.5v.01' },
  peligro: { fondo: 'var(--danger-bg)', color: 'var(--danger)', trazo: 'M8.5 8.5h7M10.2 8.5V7.3c0-.4.3-.8.8-.8h2c.4 0 .8.4.8.8v1.2M9.3 8.5l.5 8.1c0 .5.5.9 1 .9h2.4c.5 0 1-.4 1-.9l.5-8.1' },
  texto: { fondo: 'var(--gold-light)', color: 'var(--gold-dark)', trazo: 'M7.5 16.5l.6-2.6 6.6-6.6a1.4 1.4 0 0 1 2 2l-6.6 6.6-2.6.6z' },
};
function IconoDialogo({ tipo }: { tipo: Icono }) {
  const i = ICONO[tipo];
  return (
    <span className="dlg-icono" style={{ background: i.fondo, color: i.color }} aria-hidden="true">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d={i.trazo} />
      </svg>
    </span>
  );
}
